import React from 'react';
import { argsToStringifiable } from '@local/finsera-core/src/helpers/embed/utils';
import { _, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import Chart from './chart';
import { useUiStyles } from '../../../hooks';
import CenteredLoader from '../../centered-loader';
import ChartError from '../../chart-error';

type DrilldownTreemapProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  readOnly: boolean;
  category: string;
  lvl1Name: string;
  lvl1Value: string;
  lvl2Name: string;
  lvl2Value: string;
  lvl2Weight: string;
  lvl2Label: string;
  alert?: {
    type: 'error' | 'warning';
    message: string;
  };
  homeText?: string;
  exportTitle?: string;
  exportSubTitle?: string;
  download?: boolean;
  setExternalEmbed?: (_data: types.analysis.EmbedChartData) => void;
  numberFormat?: string;
  heatmapColors?: {
    startColor: number[];
    endColor: number[];
    middleColor: number[];
  };
  withSign?: boolean;
  stdDevBoundaries?: boolean;
};

const DrilldownTreemap = (args: DrilldownTreemapProps) => {
  const uiStyles = useUiStyles();

  const {
    id,
    data,
    fullScreen,
    readOnly,
    category,
    lvl1Name,
    lvl1Value,
    lvl2Name,
    lvl2Value,
    lvl2Weight,
    lvl2Label,
    numberFormat = '#.##%',
    alert,
    exportTitle,
    exportSubTitle,
    download = true,
    setExternalEmbed,
    heatmapColors = {
      startColor: [187, 60, 52],
      middleColor: [65, 69, 84],
      endColor: [38, 145, 78],
    },
    withSign = true,
    stdDevBoundaries = false,
    homeText = 'Root',
  } = args;

  const [localData, setLocalData] = React.useState<types.widgets.TableData>(null);

  React.useEffect(() => {
    const handleData = async () => {
      setLocalData(null);
      if (_.isNil(data)) return;
      await new Promise((resolve) => setTimeout(resolve, 200));

      if (setExternalEmbed)
        setExternalEmbed({
          id,
          widgetTitle: exportTitle,
          widgetSubTitle: exportSubTitle,
          chart_type: 'drilldown-treemap-chart-v5',
          args: argsToStringifiable({
            ...args,
            data,
          }),
        });

      setLocalData(data);
    };

    handleData();
  }, [data]);

  return (
    <mui.core.Box
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        display: 'grid',
        gridTemplateRows: `${alert ? 'auto ' : ''} 1fr`,
        borderRadius: 2,
        p: 1,
        pb: 0,
      }}
    >
      {alert && <ChartError readOnly={readOnly} error={alert.message} severity={alert.type} />}
      <mui.core.Box sx={uiStyles.widgetContent}>
        <mui.core.Box sx={{ height: fullScreen ? 'calc(100% - 40px)' : '100%', overflow: 'hidden' }}>
          {_.isNil(localData) ? (
            <CenteredLoader label="Loading widget data..." top="-40px" />
          ) : (
            <Chart
              id={id}
              data={localData}
              fullScreen={fullScreen}
              download={download}
              homeText={homeText}
              category={category}
              lvl1Name={lvl1Name}
              lvl1Value={lvl1Value}
              lvl2Name={lvl2Name}
              lvl2Value={lvl2Value}
              lvl2Weight={lvl2Weight}
              lvl2Label={lvl2Label}
              numberFormat={numberFormat}
              heatmapColors={heatmapColors}
              withSign={withSign}
              stdDevBoundaries={stdDevBoundaries}
            />
          )}
        </mui.core.Box>
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default DrilldownTreemap;
